import { X, FileText, Settings, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PlanningSession, PlanningState } from '@/types';

interface PlanningModeBannerProps {
  session: PlanningSession;
  onCancel: () => void;
}

const STATE_CONFIG: Record<PlanningState, { label: string; color: string; icon: typeof FileText }> = {
  gathering_requirements: {
    label: 'Gathering requirements',
    color: 'bg-blue-100 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300',
    icon: FileText,
  },
  selecting_account: {
    label: 'Select account',
    color: 'bg-blue-100 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300',
    icon: Settings,
  },
  generating_plan: {
    label: 'Generating plan',
    color: 'bg-yellow-100 border-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-800 dark:text-yellow-300',
    icon: Loader2,
  },
  awaiting_approval: {
    label: 'Review plan',
    color: 'bg-purple-100 border-purple-200 text-purple-800 dark:bg-purple-900/30 dark:border-purple-800 dark:text-purple-300',
    icon: FileText,
  },
  awaiting_confirmation: {
    label: 'Confirm execution',
    color: 'bg-orange-100 border-orange-200 text-orange-800 dark:bg-orange-900/30 dark:border-orange-800 dark:text-orange-300',
    icon: AlertCircle,
  },
  executing: {
    label: 'Executing',
    color: 'bg-green-100 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300',
    icon: Loader2,
  },
  completed: {
    label: 'Completed',
    color: 'bg-green-100 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300',
    icon: CheckCircle,
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-gray-100 border-gray-200 text-gray-800 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300',
    icon: X,
  },
};

export function PlanningModeBanner({ session, onCancel }: PlanningModeBannerProps) {
  const config = STATE_CONFIG[session.state];
  const Icon = config.icon;
  const isAnimated = session.state === 'generating_plan' || session.state === 'executing';
  const canCancel = !['completed', 'cancelled', 'executing'].includes(session.state);

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg border px-4 py-2 mb-4',
        config.color
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn('rounded-full p-1.5', session.state === 'executing' ? 'bg-green-200 dark:bg-green-800' : 'bg-white/50 dark:bg-gray-800')}>
          <Icon className={cn('h-4 w-4', isAnimated && 'animate-spin')} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide opacity-75">
              Planning Mode
            </span>
            <span className="rounded-full bg-white/50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium">
              {config.label}
            </span>
          </div>
          {session.accountName && (
            <div className="text-xs opacity-75 mt-0.5">
              Account: {session.accountName}
            </div>
          )}
        </div>
      </div>

      {canCancel && (
        <button
          onClick={onCancel}
          className="rounded-full p-1.5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          title="Cancel planning"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

interface AccountSelectorProps {
  accounts: Array<{ id: string; name: string }>;
  onSelect: (accountId: string) => void;
}

export function AccountSelector({ accounts, onSelect }: AccountSelectorProps) {
  return (
    <div className="rounded-lg border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 p-4 mb-4">
      <h3 className="font-medium mb-3">Select a Zoom account to configure:</h3>
      <div className="space-y-2">
        {accounts.map((account) => (
          <button
            key={account.id}
            onClick={() => onSelect(account.id)}
            className="w-full flex items-center gap-3 p-3 rounded-lg border bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
          >
            <Settings className="h-5 w-5 text-gray-500" />
            <div>
              <div className="font-medium">{account.name}</div>
              <div className="text-xs text-gray-500">{account.id.slice(0, 12)}...</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
